(() => {
  const sectionSelector = '[data-orbit-collection]';

  class CollectionOrbit extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.pieces = [...this.querySelectorAll('[data-collection-piece]')];
      this.stage = this.querySelector('.ro__stage');
      this.index = 0;
      this.angle = 0;
      this.target = 0;
      this.visible = true;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.querySelector('[data-ro="prev"]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-ro="next"]')?.addEventListener('click', () => this.step(1));
      this.pieces.forEach((piece, index) => piece.addEventListener('click', event => {
        if (this.dragged) { event.preventDefault(); this.dragged = false; return; }
        if (index !== this.index) { event.preventDefault(); this.go(index); }
      }));
      let start;
      this.stage?.addEventListener('dragstart', event => event.preventDefault());
      this.stage?.addEventListener('pointerdown', event => { if (!event.button) start = { x: event.clientX, angle: this.angle }; });
      this.stage?.addEventListener('pointermove', event => {
        if (!start) return;
        const dx = event.clientX - start.x;
        if (Math.abs(dx) > 6) { event.preventDefault(); if (!start.captured) { start.captured = true; this.stage.setPointerCapture(event.pointerId); } this.angle = start.angle + dx / Math.max(320, this.stage.clientWidth) * Math.PI * 1.8; this.target = this.angle; }
      });
      this.stage?.addEventListener('pointerup', event => {
        if (!start) return;
        const moved = Math.abs(event.clientX - start.x) > 6;
        this.dragged = moved;
        start = null;
        if (moved) this.snap();
      });
      if ('IntersectionObserver' in window) new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; }, { threshold: .05 }).observe(this);
      this.last = performance.now();
      this.tick = this.tick.bind(this);
      requestAnimationFrame(this.tick);
    }
    step(direction) { this.go((this.index + direction + this.pieces.length) % this.pieces.length); }
    go(index) { this.index = index; this.target = -index * Math.PI * 2 / this.pieces.length; this.updateLabels(); }
    snap() {
      const segment = Math.PI * 2 / this.pieces.length;
      this.index = ((Math.round(-this.angle / segment) % this.pieces.length) + this.pieces.length) % this.pieces.length;
      this.target = -this.index * segment;
      this.updateLabels();
    }
    updateLabels() {
      const piece = this.pieces[this.index];
      const counter = this.querySelector('[data-ro="counter"]');
      if (counter) counter.textContent = String(this.index + 1).padStart(2, '0');
      const name = this.querySelector('[data-ro="name"]');
      if (name) name.textContent = piece?.dataset.name || '';
    }
    tick(now) {
      if (!this.isConnected) return;
      const dt = Math.min(.05, (now - this.last) / 1000); this.last = now;
      if (this.visible && !document.hidden) {
        if (!this.reduced.matches && Math.abs(this.target - this.angle) < .001) this.target -= dt * Math.PI * 2 / 48;
        this.angle += (this.target - this.angle) * (1 - Math.exp(-7 * dt));
        let best = -2, front = 0;
        this.pieces.forEach((piece, index) => {
          const theta = this.angle + index * Math.PI * 2 / this.pieces.length;
          const depth = Math.cos(theta), scale = .58 + .42 * (depth + 1) / 2;
          const x = Math.sin(theta) * this.clientWidth * .23;
          const y = -depth * 28;
          piece.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${scale}) rotate(${Math.sin(theta) * 7}deg)`;
          piece.style.zIndex = String(Math.round((depth + 1) * 50));
          piece.style.opacity = String(.4 + .6 * (depth + 1) / 2);   // .ro__piece starts hidden until a script places it
          if (depth > best) { best = depth; front = index; }
        });
        if (front !== this.index && Math.abs(this.target - this.angle) < .08) { this.index = front; this.updateLabels(); }
      }
      requestAnimationFrame(this.tick);
    }
  }
  if (!customElements.get('orbit-collection-rail')) customElements.define('orbit-collection-rail', CollectionOrbit);

  document.addEventListener('click', async event => {
    const root = event.target.closest(sectionSelector) || document.querySelector(sectionSelector);
    if (!root) return;
    const drawer = root.querySelector('[data-collection-filters]');
    if (event.target.closest('[data-filter-open]')) { drawer.hidden = false; document.body.style.overflow = 'hidden'; drawer.querySelector('button, input, select, a')?.focus(); return; }
    if (event.target.closest('[data-filter-close]')) { drawer.hidden = true; document.body.style.overflow = ''; root.querySelector('[data-filter-open]')?.focus(); return; }
    const view = event.target.closest('[data-collection-view]');
    if (view) {
      const mode = view.dataset.collectionView;
      root.querySelectorAll('[data-collection-view]').forEach(button => button.classList.toggle('is-on', button === view));
      root.querySelector('[data-collection-products]').hidden = mode === 'orbit';
      root.querySelector('[data-collection-orbit]').hidden = mode !== 'orbit';
      root.querySelector('#product-grid')?.setAttribute('data-view', mode === 'large' ? 'large' : 'grid');
      return;
    }
    const more = event.target.closest('[data-load-more]');
    if (!more) return;
    event.preventDefault(); more.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(more.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error();
      const next = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = next.querySelector('#product-grid');
      const current = root.querySelector('#product-grid');
      [...incoming.children].forEach(card => current.appendChild(card));
      const nextLink = next.querySelector('[data-load-more]');
      if (nextLink) more.href = nextLink.href; else more.closest('.o-load').hidden = true;
      root.querySelector('[data-showing]').textContent = current.children.length;
      window.Orbit?.init(current);
    } catch (_) { location.assign(more.href); }
    finally { more.removeAttribute('aria-busy'); }
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const drawer = document.querySelector('[data-collection-filters]:not([hidden])');
    if (drawer) { drawer.hidden = true; document.body.style.overflow = ''; }
  });
})();
